import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useUser } from '@/shared/providers/user-provider/use-user';
import {
  STORAGE_KEY,
  LOCAL_STORAGE_UPDATED_EVENT,
} from '../model/confirm-Initializer';

type PromptState = {
  viewCount: number;
  lastPopupTime: number | null;
  showPopup: boolean;
  showBanner: boolean;
};

type UserStorageEntry = {
  userId: string | number;
  state: PromptState;
};

const initialState: PromptState = {
  viewCount: 1,
  lastPopupTime: null,
  showPopup: false,
  showBanner: false,
};

export function useConfirmDataPrompt() {
  const router = useRouter();
  const { user } = useUser();
  const userId = user?.id || '';

  const [state, setState] = useState<PromptState>(initialState);
  const lastStateRef = useRef<PromptState | null>(null);

  const getCurrentUserState = useCallback(() => {
    if (!userId) return initialState;

    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) return initialState;

      const storageArray: UserStorageEntry[] = JSON.parse(saved);
      const userEntry = storageArray.find((entry) => entry.userId === userId);

      return userEntry ? userEntry.state : initialState;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return initialState;
    }
  }, [userId]);

  const updateUserState = useCallback(
    (updates: Partial<PromptState>) => {
      if (!userId) return;

      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        const storageArray: UserStorageEntry[] = saved ? JSON.parse(saved) : [];

        const userIndex = storageArray.findIndex(
          (entry) => entry.userId === userId
        );
        const currentState =
          userIndex !== -1 ? storageArray[userIndex].state : initialState;

        const newState = { ...currentState, ...updates };

        if (JSON.stringify(currentState) === JSON.stringify(newState)) {
          return;
        }

        if (userIndex !== -1) {
          storageArray[userIndex] = { userId, state: newState };
        } else {
          storageArray.push({ userId, state: newState });
        }

        localStorage.setItem(STORAGE_KEY, JSON.stringify(storageArray));

        window.dispatchEvent(new CustomEvent(LOCAL_STORAGE_UPDATED_EVENT));
      } catch (error) {
        console.error('Error updating localStorage:', error);
      }
    },
    [userId]
  );

  useEffect(() => {
    if (!userId) return;

    const savedState = getCurrentUserState();
    lastStateRef.current = savedState;
    setState(savedState);
  }, [userId, getCurrentUserState]);

  useEffect(() => {
    if (!userId) return;

    const handleStorageUpdate = () => {
      const savedState = getCurrentUserState();

      if (JSON.stringify(lastStateRef.current) !== JSON.stringify(savedState)) {
        lastStateRef.current = savedState;
        setState(savedState);
      }
    };

    window.addEventListener(LOCAL_STORAGE_UPDATED_EVENT, handleStorageUpdate);
    window.addEventListener('storage', handleStorageUpdate);

    return () => {
      window.removeEventListener(
        LOCAL_STORAGE_UPDATED_EVENT,
        handleStorageUpdate
      );
      window.removeEventListener('storage', handleStorageUpdate);
    };
  }, [userId, getCurrentUserState]);

  const handlePopupShow = useCallback(() => {
    updateUserState({ showPopup: true });
    addQueryParam('confirm');
  }, [updateUserState]);

  const handlePopupClose = useCallback(() => {
    updateUserState({ showPopup: false });
    removeQueryParam('modal');
  }, [updateUserState]);

  const handleReset = useCallback(() => {
    if (!userId) return;

    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const storageArray: UserStorageEntry[] = JSON.parse(saved);
        const updatedStorage = storageArray.filter(
          (entry) => entry.userId !== userId
        );
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedStorage));
      }

      window.dispatchEvent(new CustomEvent(LOCAL_STORAGE_UPDATED_EVENT));
      removeQueryParam('confirm');
    } catch (error) {
      console.error('Error resetting state:', error);
    }
  }, [userId]);

  const addQueryParam = (value: string) => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('modal', value);
    window.history.replaceState({}, '', currentUrl.toString());
  };

  const removeQueryParam = (param: string) => {
    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.has(param)) {
      currentUrl.searchParams.delete(param);
      window.history.replaceState({}, '', currentUrl.toString());
    }
  };

  const role = useMemo(() => {
    if (!user?.id) return 'visitor';
    return user.emailConfirm || user.phoneConfirm ? 'player' : 'guest';
  }, [user]);

  return {
    showPopup: state.showPopup,
    showBanner: role === 'visitor' ? false : state.showBanner,
    onPopupClose: handlePopupClose,
    onPopupShow: handlePopupShow,
    addQueryParam,
    role,
    handleReset,
    removeQueryParam,
  };
}
